import React, { useEffect, useRef, useState, useCallback } from "react";
import { growFrameWhenRecaptchaClicked, stopRecaptchaGrow, VERSAPAY_CLIENT_URL } from "../utils/versapayFrame";
import { UpdateFieldFn, Golfer } from "../types/types";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "./shared/Footer";
import {
  normalizeInput,
  convertFromISOToUS,
  usDateToTimestamp,
} from "../utils/utils";
import {
  getProducts,
  createOrUpdateMember,
  createGiftPurchase,
  ApplyPromoCode,
  UpdatePromoCode,
  createVersapaySession,
  confirmVersapayPayment,
} from "../api/api-hook";
import { usePageContent } from "../api/usePageContent";

declare global {
  interface Window {
    versapay?: {
      initClient: (
        sessionId: string,
        styles?: Record<string, any>,
        fontUrls?: string[]
      ) => Promise<{
        initFrame: (container: HTMLElement, width: string, height: string) => Promise<void>;
        submitEvents: () => Promise<void>;
        onApproval?: (
          onSuccess: (result: any) => void,
          onError: (error: any) => void
        ) => void;
      }>;
    };
  }
}

interface VipProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category: string;
  tabKey: string;
  imageUrl: string;
  under_25: string;
  product_id: string;
  bagTagPrice: number;
}

interface LocationState {
  golfer?: Golfer;
}

type Step6Props = {
  formData: Golfer;
  updateField: UpdateFieldFn;
  resetStep: (step: number) => void;
  nextStep?: () => void;
  prevStep?: () => void;
};

const VERSAPAY_SCRIPT_ID = "versapay-sdk-script";
const VERSAPAY_SCRIPT_SRC = VERSAPAY_CLIENT_URL;

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

// VersaPay's SDK only fires onApproval once per SDK lifetime. We must reload
// the script between payment flows. To avoid duplicate window 'message'
// listeners from the old SDK interfering, we track every listener VersaPay
// registers so we can remove them before reloading.
let vpMessageListeners: Array<[EventListenerOrEventListenerObject, (AddEventListenerOptions | boolean | undefined)]> = [];
let vpListenerTrackingActive = false;
let versapaySessionWasUsed = false;

function startTrackingVPListeners() {
  if (vpListenerTrackingActive) return;
  vpListenerTrackingActive = true;
  const _orig = window.addEventListener.bind(window);
  (window as any).addEventListener = function (type: string, listener: any, options?: any) {
    if (type === "message") vpMessageListeners.push([listener, options]);
    return _orig(type, listener, options);
  };
}

function removeTrackedVPListeners() {
  vpMessageListeners.forEach(([l, opts]) =>
    window.removeEventListener("message", l as EventListener, opts as any)
  );
  vpMessageListeners = [];
}

// Returns true if the SDK was reloaded (old session is now invalid and must be cleared)
async function loadVersaPayScript(): Promise<boolean> {
  // Start tracking message listeners before any VersaPay code runs.
  startTrackingVPListeners();

  const existing = document.getElementById(VERSAPAY_SCRIPT_ID);
  if (existing && window.versapay && !versapaySessionWasUsed) return false; // already loaded, no reload needed

  // Remove old SDK's message listeners so they can't intercept the new session.
  removeTrackedVPListeners();
  if (existing) existing.remove();
  try { (window as any).versapay = undefined; } catch (_) {}
  versapaySessionWasUsed = false;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = VERSAPAY_SCRIPT_ID;
    script.src = `${VERSAPAY_SCRIPT_SRC}?_t=${Date.now()}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load VersaPay SDK."));
    document.body.appendChild(script);
  });

  return true; // script was reloaded — caller must discard any cached session
}

export default function Step6({ formData, updateField, resetStep }: Step6Props) {
  const { title: pageTitle, overview: pageOverview } = usePageContent("step6");
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const [error, setError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<VipProduct[]>([]);
  const [approved, setApproved] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const [sameAsBilling, setSameAsBilling] = useState<boolean>(
    !(state.golfer?.GiftPurchase ?? formData.GiftPurchase)
  );

  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState<boolean>(
    !!(formData.promo_code && Number(formData.promo_amount_applied) > 0)
  );
  const [promoError, setPromoError] = useState<string | null>(null);
  const [giftValue, setGiftValue] = useState<number | null>(
    Number(formData.promo_value) > 0 ? Number(formData.promo_value) : null
  );
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(
    formData.promo_code || null
  );

  const [golfer, setGolfer] = useState<Golfer | null>(state.golfer ?? null);

  // ─── Stable refs — always current, never stale in callbacks ─────────────
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const sameAsBillingRef = useRef(sameAsBilling);
  sameAsBillingRef.current = sameAsBilling;

  const golferRef = useRef(golfer);
  golferRef.current = golfer;

  const finalTotalRef = useRef(0);
  const finalTotalCentsRef = useRef(0);
  const vipUnitPriceCentsRef = useRef(0);  // unit price of VIP card in cents — for line items

  // ─── Lifecycle / VersaPay refs ───────────────────────────────────────────
  const mountedRef = useRef(true);
  const handleApprovalRef = useRef<((result: any, sessionId: string) => Promise<void>) | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Order summary starts collapsed on phones/tablets; always open on desktop
  const [summaryOpen, setSummaryOpen] = useState(false);
  const clientRef = useRef<{
    initFrame: (container: HTMLElement, width: string, height: string) => Promise<void>;
    submitEvents: () => Promise<void>;
    onApproval?: (onSuccess: (result: any) => void, onError: (error: any) => void) => void;
  } | null>(null);

  // In-memory session state only — cleared on every page refresh automatically
  const sessionIdRef = useRef<string>("");
  const sessionAmountRef = useRef<number>(0);
  const setupInFlightRef = useRef(false);
  const processingApprovalRef = useRef(false);
  const frameMountedRef = useRef(false);
  const callbackBoundSessionRef = useRef<string>("");
  const mountedSessionRef = useRef<string>("");
  const didHydrateGolferRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ─── Hydrate formData from location state — once on mount ───────────────
  useEffect(() => {
    if (!state.golfer || didHydrateGolferRef.current) return;
    didHydrateGolferRef.current = true;

    updateField("id", (state.golfer?.id as any) || "");
    updateField("firstName", state.golfer?.firstName || "");
    updateField("lastName", state.golfer?.lastName || "");
    updateField("email", state.golfer?.email || "");
    updateField("phone", normalizeInput(state.golfer?.phone));
    updateField("sex", state.golfer?.sex || "");

    if (state.golfer?.date_of_birth && state.golfer.date_of_birth.includes("-")) {
      updateField("date_of_birth", convertFromISOToUS(state.golfer.date_of_birth));
    } else {
      updateField("date_of_birth", state.golfer?.date_of_birth || "");
    }

    updateField("address", state.golfer?.address || "");
    updateField("street_address_2", state.golfer?.street_address_2 || "");
    updateField("city", state.golfer?.city || "");
    updateField("state", state.golfer?.state || "");
    updateField("zip", state.golfer?.zip || "");

    const isGiftPurchase = !!state.golfer?.GiftPurchase;
    const sab =
      typeof state.golfer?.sameAsBilling === "boolean"
        ? state.golfer.sameAsBilling
        : !isGiftPurchase;

    setSameAsBilling(sab);
    updateField("sameAsBilling" as any, sab);

    updateField("ShippingAddress", state.golfer?.ShippingAddress || "");
    updateField("ShippingStreet_address_2", state.golfer?.ShippingStreet_address_2 || "");
    updateField("ShippingCity", state.golfer?.ShippingCity || "");
    updateField("ShippingState", state.golfer?.ShippingState || "");
    updateField("ShippingZip", state.golfer?.ShippingZip || "");

    updateField("bagTag", !!state.golfer?.bagTag);
    updateField("donation", !!state.golfer?.donation);
    updateField("personalization", state.golfer?.personalization || "");
    updateField("donation_amount", state.golfer?.donation_amount || 0);

    updateField("vip_card_type", state.golfer?.vip_card_type || "");
    updateField("vip_package", state.golfer?.vip_package || "");
    updateField("quantity", (state.golfer?.quantity as any) || 1);

    updateField("GiftPurchase", state.golfer?.GiftPurchase || false);
    updateField("GiftProductId", state.golfer?.GiftProductId || "");
    updateField("GiftVipPackage", state.golfer?.GiftVipPackage || "");
    updateField("ghinNumber", state.golfer?.ghinNumber || "");
    updateField("product_id", state.golfer?.product_id || "");
    updateField("bagTagPrice", state.golfer?.bagTagPrice || 0);
  }, []); // intentionally empty — hydrate once on mount only

  // ─── Derived totals ──────────────────────────────────────────────────────
  const selectedVipProduct = products.length > 0 ? products[0] : null;
  const unitPrice = Number(selectedVipProduct?.price) || 0;
  const quantity = Number(formData.quantity) || 1;
  const bagTagUnitPrice = Number(formData.bagTagPrice) || 0;
  const bagTagTotal = formData.bagTag ? bagTagUnitPrice : 0;
  const donationTotal = formData.donation ? Number(formData.donation_amount) || 0 : 0;
  const cartTotal = unitPrice * quantity + bagTagTotal + donationTotal;
  const discount = Number(formData.promo_amount_applied || 0);
  const finalTotal = Math.max(0, cartTotal - discount);
  const finalTotalCents = Math.round(finalTotal * 100);
  const isGift = !!formData.GiftPurchase;

  // Keep refs current so approval callback never reads stale values
  finalTotalRef.current = finalTotal;
  finalTotalCentsRef.current = finalTotalCents;
  vipUnitPriceCentsRef.current = Math.round(unitPrice * 100);

  // ─── Sync cart_total to formData ─────────────────────────────────────────
  const cartTotalSyncedRef = useRef<number | null>(null);
  useEffect(() => {
    if (cartTotalSyncedRef.current === finalTotal) return;
    cartTotalSyncedRef.current = finalTotal;
    updateField("cart_total", finalTotal as any);
  }, [finalTotal]);

  // ─── Load products ────────────────────────────────────────────────────────
  const giftVipPackage = state.golfer?.GiftVipPackage || "";
  const vipPackage = state.golfer?.vip_package || "";
  const isGiftPurchase = !!golfer?.GiftPurchase;

  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      try {
        setError(null);
        const raw = isGiftPurchase
          ? await getProducts({ property: "tabKey", value: giftVipPackage })
          : await getProducts({ property: "tabKey", value: vipPackage });

        if (cancelled) return;
        const data: VipProduct[] = typeof raw === "string" ? JSON.parse(raw) : raw;
        setProducts(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Failed to load products");
      }
    }

    loadProducts();
    return () => { cancelled = true; };
  }, [isGiftPurchase, giftVipPackage, vipPackage]);

  // ─── Session management — in-memory only ────────────────────────────────
  const clearCheckoutSession = useCallback(() => {
    // Immediately wipe the iframe DOM so the stale frame isn't interactive
    // while the new session loads.
    if (containerRef.current) containerRef.current.innerHTML = "";
    clientRef.current = null;
    frameMountedRef.current = false;
    callbackBoundSessionRef.current = "";
    mountedSessionRef.current = "";
    sessionIdRef.current = "";
    sessionAmountRef.current = 0;
  }, []);

  const refreshCheckoutSession = useCallback(() => {
    clearCheckoutSession();
    setPageLoading(true);   // disable Submit immediately; setup will clear it
    setRetryNonce((n) => n + 1);
  }, [clearCheckoutSession]);

  // ─── Approval handler — reads everything from refs, never stale ──────────
  const handleApproval = useCallback(
    async (result: any, sessionToUse: string) => {
      if (processingApprovalRef.current) return;

      try {
        processingApprovalRef.current = true;
        setProcessingPayment(true);
        setLoading(true);
        setError(null);

        const fd = formDataRef.current;
        const sab = sameAsBillingRef.current;
        const currentGolfer = golferRef.current;
        const currentFinalTotal = finalTotalRef.current;
        const currentFinalTotalCents = finalTotalCentsRef.current;

        const billingAddress1 = sab ? fd.address : fd.ShippingAddress;
        const billingCity = sab ? fd.city : fd.ShippingCity;
        const billingState = sab ? fd.state : fd.ShippingState;
        const billingZip = sab ? fd.zip : fd.ShippingZip;

        // Build line items to mirror exactly what the user sees in the cart
        const lineItems: Array<{
          type: string;
          number: string;
          description: string;
          price: number;  // cents — matches VersaPay working payload
          quantity: number;
          discount: number;
        }> = [];

        // VIP card line — price in cents, quantity from form
        const vipUnitPriceCents = vipUnitPriceCentsRef.current;
        const vipQty = Number(fd.quantity) || 1;
        if (vipUnitPriceCents > 0) {
          lineItems.push({
            type: "Item",
            number: fd.product_id || fd.GiftProductId || "VIP",
            description: fd.vip_package || "",
            price: vipUnitPriceCents,
            quantity: vipQty,
            discount: 0,
          });
        }

        // Bag tag line
        const bagTagCents = Math.round(Number(fd.bagTagPrice || 0) * 100);
        if (fd.bagTag && bagTagCents > 0) {
          lineItems.push({
            type: "Item",
            number: "BAG-TAG",
            description: fd.personalization
              ? `Bag Tag — ${fd.personalization}`
              : "Bag Tag",
            price: bagTagCents,
            quantity: 1,
            discount: 0,
          });
        }

        // Donation line
        const donationCents = Math.round(Number(fd.donation_amount || 0) * 100);
        if (fd.donation && donationCents > 0) {
          lineItems.push({
            type: "Item",
            number: "DONATION",
            description: "Donation",
            price: donationCents,
            quantity: 1,
            discount: 0,
          });
        }

        // Promo discount line
        const discountCents = Math.round(Number(fd.promo_amount_applied || 0) * 100);
        if (discountCents > 0) {
          lineItems.push({
            type: "Item",
            number: "PROMO",
            description: fd.promo_code
              ? `Promo: ${fd.promo_code}`
              : "Promo Discount",
            price: -discountCents,  // negative = discount
            quantity: 1,
            discount: 0,
          });
        }

        // Fallback: if nothing resolved (e.g. price missing from product),
        // send a single catch-all line so the transaction still goes through
        if (lineItems.length === 0) {
          lineItems.push({
            type: "Item",
            number: "VIP",
            description: "VIP Golf Card",
            price: currentFinalTotalCents,
            quantity: 1,
            discount: 0,
          });
        }

        const confirmResponse = await confirmVersapayPayment({
          sessionId: sessionToUse,
          approvalResult: result,
          amount: currentFinalTotalCents,
          firstName: fd.firstName,
          lastName: fd.lastName,
          email: fd.email,
          address1: billingAddress1,
          city: billingCity,
          state: billingState,
          zip: billingZip,
          vipCardNumber: fd.ghinNumber || "",  // maps ghinNumber → vipCardNumber
          lineItems,
        } as any);

        if (!confirmResponse?.success) {
          throw new Error(confirmResponse?.error || "Failed to confirm payment.");
        }

        updateField("versapayApprovalResult" as any, result);
        updateField("versapayTransactionId" as any, confirmResponse.transactionId);
        updateField("versapayOrderNumber" as any, confirmResponse.orderNumber ?? "");
        updateField("versapayVpOrderNumber" as any, confirmResponse.vpOrderNumber ?? "");
        updateField("ccToken" as any, confirmResponse.ccToken ?? "");
        updateField("ccLastFour" as any, confirmResponse.ccLastFour ?? "");
        updateField("ccType" as any, confirmResponse.ccType ?? "");
        setApproved(true);

        // Regular flow: golfer state is null (data lives in formData props, not location state).
        // All fields below are explicitly set from fd so the spread is just a safety base.
        const updatedGolfer: Golfer = {
          ...(currentGolfer ?? ({} as Golfer)),
          firstName: fd.firstName || "",
          lastName: fd.lastName || "",
          email: fd.email || "",
          phone: fd.phone || "",
          sex: fd.sex || "",
          date_of_birth: fd.date_of_birth || "",
          address: fd.address || "",
          street_address_2: fd.street_address_2 || "",
          city: fd.city || "",
          state: fd.state || "",
          zip: fd.zip || "",
          id: (fd.id as any) || "",
          bagTag: fd.bagTag || false,
          donation: fd.donation || false,
          personalization: fd.personalization || "",
          donation_amount: fd.donation_amount || 0,
          vip_package: fd.vip_package || "",
          vip_card_type: fd.vip_card_type || "",
          cart_total: currentFinalTotal,
          quantity: (fd.quantity as any) || "",
          sameAsBilling: sab,
          ShippingAddress: fd.ShippingAddress || "",
          ShippingStreet_address_2: fd.ShippingStreet_address_2 || "",
          ShippingCity: fd.ShippingCity || "",
          ShippingState: fd.ShippingState || "",
          ShippingZip: fd.ShippingZip || "",
          GiftPurchase: fd.GiftPurchase || false,
          GiftProductId: fd.GiftProductId || "",
          GiftVipPackage: fd.GiftVipPackage || "",
          ghinNumber: fd.ghinNumber || "",
          product_id: fd.product_id || "",
          bagTagPrice: fd.bagTagPrice || 0,
          promo_object_id: fd.promo_object_id || 0,
          promo_code: fd.promo_code || "",
          promo_amount_applied: fd.promo_amount_applied || 0,
          promo_value: fd.promo_value || 0,
          versapaySessionId: sessionToUse as any,
          versapayApprovalResult: result as any,
          versapayTransactionId: confirmResponse.transactionId as any,
          versapayOrderNumber: (confirmResponse.orderNumber ?? "") as any,
          versapayVpOrderNumber: (confirmResponse.vpOrderNumber ?? "") as any,
          ccToken:    (confirmResponse.ccToken    ?? "") as any,
          ccLastFour: (confirmResponse.ccLastFour ?? "") as any,
          ccType:     (confirmResponse.ccType     ?? "") as any,
        };

        const hubspotPayload = {
          ...updatedGolfer,
          date_of_birth: updatedGolfer.date_of_birth
            ? usDateToTimestamp(updatedGolfer.date_of_birth)
            : undefined,
        };

        if (!!updatedGolfer.GiftPurchase) {
          await createGiftPurchase({ ...hubspotPayload, payment_method: "Credit Card" } as any);
        } else {
          await createOrUpdateMember(hubspotPayload as any);
        }

        setGolfer(updatedGolfer);

        navigate("/confirm", {
          replace: true,
          state: { ...location.state, golfer: updatedGolfer },
        });
      } catch (err: any) {
        console.error("Payment processing failed:", err);

        const status = err?.response?.status;
        const bodyText: string = err?.response?.data ?? err?.message ?? "";
        const is504 =
          status === 504 ||
          (typeof bodyText === "string" && bodyText.includes("504")) ||
          (typeof bodyText === "string" && bodyText.toLowerCase().includes("gateway time-out"));

        setError(
          is504
            ? "The payment request timed out. Your card has not been charged — please wait a moment and try again."
            : err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Payment was approved but processing failed."
        );
      } finally {
        processingApprovalRef.current = false;
        setProcessingPayment(false);
        setLoading(false);
      }
    },
    [navigate, location.state, updateField]
  );

  // Keep handleApprovalRef current so the VersaPay onApproval closure never goes stale
  handleApprovalRef.current = handleApproval;

  // ─── VersaPay setup effect ────────────────────────────────────────────────
  // Design notes:
  // - sessionIdRef / sessionAmountRef are module-level refs that survive
  //   StrictMode's unmount/remount cycle — so we never create two sessions.
  // - We do NOT use a local `cancelled` flag because StrictMode sets it to
  //   true on the first run's cleanup, which would prevent the remounted
  //   run from completing. Instead we gate on mountedRef.current (set false
  //   only on real unmount) and setupInFlightRef.
  useEffect(() => {
    const setupCheckout = async () => {
      // Wait for container and products
      if (!containerRef.current || !products.length) {
        setPageLoading(true);
        return;
      }

      // No chargeable amount yet
      if (!finalTotalCents || finalTotalCents <= 0) {
        setPageLoading(false);
        return;
      }

      // Frame already mounted for this exact session + amount — nothing to do
      if (
        frameMountedRef.current &&
        sessionIdRef.current &&
        mountedSessionRef.current === sessionIdRef.current &&
        sessionAmountRef.current === finalTotalCents
      ) {
        setPageLoading(false);
        return;
      }

      // Session already exists in memory for this amount — reuse it.
      // This is the key StrictMode guard: refs survive unmount/remount,
      // so the second invoke finds the session and skips createVersapaySession.
      const sessionAlreadyExists =
        !!sessionIdRef.current && sessionAmountRef.current === finalTotalCents;

      // Only block if in-flight AND we have no existing session to fall back on
      if (setupInFlightRef.current && !sessionAlreadyExists) return;

      setupInFlightRef.current = true;
      setPageLoading(true);
      setError(null);

      try {
        // ── Resolve session — reuse ref or create new ─────────────────────
        let sessionToUse = sessionAlreadyExists ? sessionIdRef.current : "";

        if (!sessionToUse) {
          const fd = formDataRef.current;
          const sessionResponse = await createVersapaySession({
            amount: finalTotalCents,
            firstName: fd.firstName,
            lastName: fd.lastName,
            email: fd.email,
          });

          if (!sessionResponse?.sessionId) throw new Error("No VersaPay session ID returned.");

          sessionToUse = sessionResponse.sessionId;
          sessionIdRef.current = sessionToUse;
          sessionAmountRef.current = finalTotalCents;
        }

        // Stop if component genuinely unmounted (not StrictMode remount)
        if (!mountedRef.current || !containerRef.current) return;

        // ── Load SDK script ───────────────────────────────────────────────
        const sdkReloaded = await loadVersaPayScript();

        // If the SDK was reloaded, the old session is dead — force a fresh one
        if (sdkReloaded) {
          sessionIdRef.current = "";
          sessionAmountRef.current = 0;
          sessionToUse = "";
        }

        if (!mountedRef.current || !containerRef.current) return;

        if (!window.versapay) throw new Error("VersaPay SDK did not load.");

        // Create a new session if the old one was discarded
        if (!sessionToUse) {
          const fd = formDataRef.current;
          const sessionResponse = await createVersapaySession({
            amount: finalTotalCents,
            firstName: fd.firstName,
            lastName: fd.lastName,
            email: fd.email,
          });
          if (!sessionResponse?.sessionId) throw new Error("No VersaPay session ID returned.");
          sessionToUse = sessionResponse.sessionId;
          sessionIdRef.current = sessionToUse;
          sessionAmountRef.current = finalTotalCents;
        }

        if (!mountedRef.current || !containerRef.current) return;

        // ── Init client ───────────────────────────────────────────────────
        const client = await window.versapay.initClient(sessionToUse);
        clientRef.current = client;

        if (!mountedRef.current || !containerRef.current) return;

        // ── Bind approval callback BEFORE initFrame ──────────────────────
        // VersaPay uses onApproval to wire up its reCAPTCHA response handler;
        // it must be registered before initFrame loads the iframe.
        if (client.onApproval) {
          const boundSession = sessionToUse;
          client.onApproval(
            (result: any) => {
              stopRecaptchaGrow();
              handleApprovalRef.current?.(result, boundSession);
            },
            (err: any) => {
              stopRecaptchaGrow();
              console.error("[VP] onApproval ERROR:", err);
              const vpIframe = containerRef.current?.querySelector("iframe");
              if (vpIframe) vpIframe.style.height = "270px";
              const msg =
                typeof err === "string" ? err :
                err?.message || err?.error || err?.description ||
                (typeof err === "object" ? JSON.stringify(err) : null);
              setError(msg || "Payment was not approved. Please try again.");
              setProcessingPayment(false);
              setLoading(false);
            }
          );
          callbackBoundSessionRef.current = sessionToUse;
        }

        // ── Mount iframe ──────────────────────────────────────────────────
        if (!frameMountedRef.current || mountedSessionRef.current !== sessionToUse) {
          containerRef.current.innerHTML = "";
          await client.initFrame(containerRef.current, "600px", "270px");

          // Check again after the async initFrame call
          if (!mountedRef.current || !containerRef.current) return;

          // Override VersaPay's fixed dimensions so the iframe fills width and stays compact
          const vpIframe = containerRef.current.querySelector("iframe");
          if (vpIframe) {
            vpIframe.style.width = "100%";
            vpIframe.style.maxWidth = "100%";
            vpIframe.style.height = "270px";
          }

          frameMountedRef.current = true;
          mountedSessionRef.current = sessionToUse;
        }
      } catch (err: any) {
        if (mountedRef.current) {
          console.error("VersaPay setup failed:", err);
          setError(err?.response?.data?.error || err?.message || "Failed to load VersaPay checkout.");
          frameMountedRef.current = false;
          mountedSessionRef.current = "";
        }
      } finally {
        setupInFlightRef.current = false;
        if (mountedRef.current) setPageLoading(false);
      }
    };

    setupCheckout();

    // No cleanup needed — mountedRef.current handles real unmount,
    // and we intentionally let StrictMode's remount complete fully.
  }, [products.length, finalTotalCents, retryNonce]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleRetry = () => {
    setApproved(false);
    setError(null);
    refreshCheckoutSession();
  };

  const handlePromoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoError(null);

    if (promoApplied) {
      setPromoApplied(false);
      setGiftValue(null);
      setAppliedPromoCode(null);
      updateField("promo_code" as any, null);
      updateField("promo_value" as any, null);
      updateField("promo_amount_applied" as any, null);
    }

    setPromoInput(e.target.value);
  };

  const applyPromoCode = async () => {
    if (isApplyingPromo) return;
    setPromoError(null);

    const code = promoInput.trim();
    if (!code) { setPromoError("Enter a promo code."); return; }

    const selectedSku =
      selectedVipProduct?.product_id || formData.product_id || formData.GiftProductId || "";

    if (!selectedSku) {
      setPromoError("Please select a package before applying a promo.");
      return;
    }

    try {
      setIsApplyingPromo(true);
      const data: any = await ApplyPromoCode(code, Number(cartTotal), Number(selectedSku), unitPrice * quantity);

      if (!data?.success) {
        const errCode = data?.error;
        const pv = Number(data?.promo_value || 0);

        if (errCode === "not_found") setPromoError("That promo code was not found.");
        else if (errCode === "promo_inactive") setPromoError("This promo code is not currently active. Please check with the club for a valid code.");
        else if (errCode === "not_active") setPromoError("This promo code is already reserved or has been used.");
        else if (errCode === "cart_too_low") setPromoError(pv ? `This promo is worth $${pv.toFixed(2)} and can only be used on orders of $${pv.toFixed(2)} or more.` : "This promo can only be used on a higher order total.");
        else if (errCode === "wrong_sku") setPromoError("This promo is only valid for the Bronze 18–25 package.");
        else if (errCode === "invalid_cart_total") setPromoError("Your cart total is invalid. Please refresh and try again.");
        else if (errCode === "promo_code_required") setPromoError("Enter a promo code.");
        else if (errCode === "sku_required") setPromoError("Please select a package before applying a promo.");
        else setPromoError("We couldn't apply that promo code. Please try again.");

        setPromoApplied(false);
        setAppliedPromoCode(null);
        setGiftValue(null);
        return;
      }

      const promoValue = Number(data.amountOff ?? 0);
      const reserved = !!data.reserved;

      if (!Number.isFinite(promoValue) || promoValue <= 0) {
        setPromoError("That promo code is invalid.");
        setPromoApplied(false); setAppliedPromoCode(null); setGiftValue(null);
        return;
      }

      if (Number(cartTotal) < promoValue) {
        setPromoError(`This promo is worth $${promoValue.toFixed(2)} and can only be used on orders of $${promoValue.toFixed(2)} or more.`);
        setPromoApplied(false); setAppliedPromoCode(null); setGiftValue(null);
        return;
      }

      updateField("promo_code" as any, code);
      updateField("promo_value" as any, promoValue);
      updateField("promo_amount_applied" as any, promoValue);
      updateField("promo_reserved" as any, reserved);
      if (data.promo_object_id) updateField("promo_object_id" as any, String(data.promo_object_id));

      setPromoApplied(true);
      setGiftValue(promoValue);
      setAppliedPromoCode(code.toUpperCase());
      setPromoInput("");
      setPromoError(null);

      refreshCheckoutSession();
    } catch {
      setPromoError("We couldn't apply that promo code. Please try again.");
      setPromoApplied(false); setAppliedPromoCode(null); setGiftValue(null);
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromo = async () => {
    const codeToRelease = appliedPromoCode;
    if (!codeToRelease) return;
    try {
      await UpdatePromoCode(codeToRelease, "Active");
    } finally {
      setPromoApplied(false); setGiftValue(null); setAppliedPromoCode(null); setPromoError(null);
      updateField("promo_code" as any, null);
      updateField("promo_value" as any, null);
      updateField("promo_amount_applied" as any, null);
      refreshCheckoutSession();
    }
  };

  const removeBagTag = (e: React.MouseEvent) => {
    e.preventDefault();
    updateField("bagTag", false);
    updateField("personalization", "");
    const updated = { ...(golfer ?? {}), bagTag: false, personalization: "" };
    setGolfer(updated);
    navigate(".", { replace: true, state: { ...location.state, golfer: updated } });
    refreshCheckoutSession();
  };

  const removeDonation = (e: React.MouseEvent) => {
    e.preventDefault();
    updateField("donation", false);
    updateField("donation_amount", 0);
    const updated = { ...(golfer ?? {}), donation: false, donation_amount: 0 };
    setGolfer(updated);
    navigate(".", { replace: true, state: { ...location.state, golfer: updated } });
    refreshCheckoutSession();
  };

  const handleFieldChange =
    (field: keyof Golfer) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const target = e.target;
      let value: any = target.value;
      if (target instanceof HTMLInputElement && target.type === "checkbox") value = target.checked;
      updateField(field, value);
      setGolfer((prev) => {
        const updated: Golfer = { ...(prev ?? ({} as Golfer)) };
        (updated as any)[field] = value;
        return updated;
      });
    };

  const formatPhoneNumber = (event: React.KeyboardEvent<HTMLInputElement>) => {
    updateField("phone", normalizeInput(event.currentTarget.value || ""));
  };

  const handleFreeOrder = async () => {
    try {
      setError(null);
      if (loading || processingPayment) return;
      setProcessingPayment(true);
      setLoading(true);

      const fd = formDataRef.current;
      const sab = sameAsBillingRef.current;
      const currentGolfer = golferRef.current;

      const updatedGolfer: Golfer = {
        ...(currentGolfer ?? ({} as Golfer)),
        firstName: fd.firstName || "",
        lastName: fd.lastName || "",
        email: fd.email || "",
        phone: fd.phone || "",
        sex: fd.sex || "",
        date_of_birth: fd.date_of_birth || "",
        address: fd.address || "",
        street_address_2: fd.street_address_2 || "",
        city: fd.city || "",
        state: fd.state || "",
        zip: fd.zip || "",
        id: (fd.id as any) || "",
        bagTag: fd.bagTag || false,
        donation: fd.donation || false,
        personalization: fd.personalization || "",
        donation_amount: fd.donation_amount || 0,
        vip_package: fd.vip_package || "",
        vip_card_type: fd.vip_card_type || "",
        cart_total: 0,
        quantity: (fd.quantity as any) || "",
        sameAsBilling: sab,
        ShippingAddress: fd.ShippingAddress || "",
        ShippingStreet_address_2: fd.ShippingStreet_address_2 || "",
        ShippingCity: fd.ShippingCity || "",
        ShippingState: fd.ShippingState || "",
        ShippingZip: fd.ShippingZip || "",
        GiftPurchase: fd.GiftPurchase || false,
        GiftProductId: fd.GiftProductId || "",
        GiftVipPackage: fd.GiftVipPackage || "",
        ghinNumber: fd.ghinNumber || "",
        product_id: fd.product_id || "",
        bagTagPrice: fd.bagTagPrice || 0,
        promo_object_id: fd.promo_object_id || 0,
        promo_code: fd.promo_code || "",
        promo_amount_applied: fd.promo_amount_applied || 0,
        promo_value: fd.promo_value || 0,
      };

      const hubspotPayload = {
        ...updatedGolfer,
        date_of_birth: updatedGolfer.date_of_birth
          ? usDateToTimestamp(updatedGolfer.date_of_birth)
          : undefined,
      };

      if (!!updatedGolfer.GiftPurchase) {
        await createGiftPurchase(hubspotPayload as any);
      } else {
        await createOrUpdateMember(hubspotPayload as any);
      }

      setGolfer(updatedGolfer);
      setApproved(true);
      navigate("/confirm", {
        replace: true,
        state: { ...location.state, golfer: updatedGolfer },
      });
    } catch (err: any) {
      console.error("Free order processing failed:", err);
      setError(err?.response?.data?.error || err?.message || "Order processing failed. Please try again.");
    } finally {
      setProcessingPayment(false);
      setLoading(false);
    }
  };

  const handlePay = async () => {
    try {
      setError(null);
      if (loading || processingPayment || pageLoading) return;
      if (!clientRef.current) throw new Error("Payment form is not ready yet.");
      setProcessingPayment(true);
      versapaySessionWasUsed = true;
      await clientRef.current.submitEvents();
      // reCAPTCHA is now visible — grow the frame once the checkbox is clicked
      growFrameWhenRecaptchaClicked(containerRef.current?.querySelector("iframe"));
    } catch (err: any) {
      console.error("VersaPay submit failed:", err);
      stopRecaptchaGrow();
      // Collapse iframe back — reCAPTCHA never showed
      const vpIframe = containerRef.current?.querySelector("iframe");
      if (vpIframe) vpIframe.style.height = "270px";
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message;
      setError(
        apiMessage
          ? `We couldn't process your payment. ${apiMessage}`
          : "We couldn't process your payment. Please double-check your details and try again."
      );
    } finally {
      if (!processingApprovalRef.current) {
        setProcessingPayment(false);
        setLoading(false);
      }
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container mt-5">
      {pageLoading && (
        <div className="vip-loading-overlay">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" />
            <p className="fw-semibold">Loading secure checkout…</p>
            <small className="text-muted">Please wait while we prepare your payment form</small>
          </div>
        </div>
      )}

      {loading && (
        <div className="vip-loading-overlay">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" />
            <p className="fw-semibold">Processing your payment…</p>
            <small className="text-muted">Please do not refresh or use the back button</small>
          </div>
        </div>
      )}

      <section className="vip-checkout container my-5">
        {approved && !error && (
          <div className="alert alert-success" role="alert">
            Payment approved. Finalizing your order…
          </div>
        )}

        <div className="row justify-content-center">
          <div className="col-12">
            <h3 className="mb-3 text-uppercase">{pageTitle || "Purchase a VIP Card"}</h3>
            {pageOverview && <p className="mb-4">{pageOverview}</p>}
            <p className="mb-4">
              The VIP Card offers golfers exclusive discounted rates at approximately 200 courses
              throughout Virginia and West Virginia. A portion of the proceeds go to support the
              VSGA-VIP Scholarship Foundation.
            </p>

            <div className="">
              <div className="card-body">
                <div
                  className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold d-none d-lg-block"
                  style={{ fontSize: "0.9rem" }}
                >
                  Checkout Information
                </div>

                <div className="row g-4">
                  {/* ── Left: form ── */}
                  <div className="col-lg-7">
                    <form id="step6-form" className="row gy-3" onSubmit={(e) => e.preventDefault()}>
                      <div className="col-12 d-lg-none">
                        <div className="bg-light border px-3 py-2 text-uppercase fw-semibold" style={{ fontSize: "0.9rem" }}>
                          Checkout Information
                        </div>
                      </div>

                      <div className="col-12 d-flex justify-content-between align-items-baseline">
                        <h5 className="mb-1 text-uppercase">VIP Cardholder Information</h5>
                        <span className="text-danger small fw-semibold">* Required</span>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small" htmlFor="firstName">
                          <span className="text-danger small fw-semibold">*</span> First Name
                        </label>
                        <input type="text" id="firstName" name="firstName" className="form-control"
                          placeholder="First Name" value={formData.firstName}
                          onChange={handleFieldChange("firstName")} required />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small" htmlFor="lastName">
                          <span className="text-danger small fw-semibold">*</span> Last Name
                        </label>
                        <input type="text" id="lastName" name="lastName" className="form-control"
                          placeholder="Last Name" value={formData.lastName}
                          onChange={handleFieldChange("lastName")} required />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small" htmlFor="email">
                          <span className="text-danger small fw-semibold">*</span> Email Address
                        </label>
                        <input type="email" id="email" name="email" className="form-control"
                          placeholder="email@example.com" value={formData.email}
                          onChange={handleFieldChange("email")} required />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small" htmlFor="phone">
                          <span className="text-danger small fw-semibold">*</span> Phone Number
                        </label>
                        <input type="tel" id="phone" name="phone" className="form-control"
                          placeholder="(555) 555-5555" value={formData.phone}
                          onChange={handleFieldChange("phone")} pattern="[0-9\(\)\-\s]{14,14}"
                          onKeyUp={formatPhoneNumber} minLength={14} maxLength={14} required />
                      </div>

                      <div className="col-12 mt-5 pt-1">
                        {!isGift ? (
                          <>
                            <h5 className="mb-2 text-uppercase">Billing Information</h5>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input" type="checkbox" id="sameAsBilling"
                                checked={sameAsBilling}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSameAsBilling(checked);
                                  updateField("sameAsBilling" as any, checked);
                                  setGolfer((prev) => prev ? { ...prev, sameAsBilling: checked } : prev);
                                }}
                              />
                              <label className="form-check-label small" htmlFor="sameAsBilling">
                                Use Shipping address as billing address
                              </label>
                            </div>
                          </>
                        ) : (
                          <h5 className="mb-2 text-uppercase">Billing Information</h5>
                        )}
                      </div>

                      {(isGift || !sameAsBilling) && (
                        <>
                          <div className="col-md-6 mt-3">
                            <label className="form-label small" htmlFor="ShippingAddress">
                              <span className="text-danger small fw-semibold">*</span>{" "}
                              {isGift ? "Shipping Address" : "Billing Address"}
                            </label>
                            <input type="text" id="ShippingAddress" name="ShippingAddress"
                              className="form-control" placeholder="Street address"
                              value={formData.ShippingAddress || ""}
                              onChange={handleFieldChange("ShippingAddress")} required />
                          </div>

                          <div className="col-md-6 mt-3">
                            <label className="form-label small" htmlFor="ShippingStreet_address_2">
                              Address Line 2
                            </label>
                            <input type="text" id="ShippingStreet_address_2"
                              name="ShippingStreet_address_2" className="form-control"
                              placeholder="Apartment, suite, etc. (optional)"
                              value={formData.ShippingStreet_address_2 || ""}
                              onChange={handleFieldChange("ShippingStreet_address_2")} />
                          </div>

                          <div className="col-md-4 mt-3">
                            <label className="form-label small" htmlFor="ShippingCity">
                              <span className="text-danger small fw-semibold">*</span> City
                            </label>
                            <input type="text" id="ShippingCity" name="ShippingCity"
                              className="form-control" placeholder="City"
                              value={formData.ShippingCity || ""}
                              onChange={handleFieldChange("ShippingCity")} required />
                          </div>

                          <div className="col-md-4 mt-3">
                            <label className="form-label small" htmlFor="ShippingState">
                              <span className="text-danger small fw-semibold">*</span> State
                            </label>
                            <select id="ShippingState" name="ShippingState"
                              className="form-select"
                              value={formData.ShippingState || ""}
                              onChange={handleFieldChange("ShippingState") as any} required>
                              <option value="">Select state…</option>
                              {US_STATES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-4 mt-3">
                            <label className="form-label small" htmlFor="ShippingZip">
                              <span className="text-danger small fw-semibold">*</span> ZIP Code
                            </label>
                            <input type="text" id="ShippingZip" name="ShippingZip"
                              className="form-control" placeholder="ZIP"
                              value={formData.ShippingZip || ""}
                              onChange={handleFieldChange("ShippingZip")} required />
                          </div>
                        </>
                      )}

                      <div className="col-12 mt-5">
                        <h5 className="mb-2 text-uppercase">Payment Information</h5>
                      </div>

                      <div
                        ref={containerRef}
                        id="versapay-checkout-container"
                        style={{ minHeight: "50px", width: "100%" }}
                      />

                      {error && (
                        <div className="alert alert-danger mt-3" role="alert">
                          {error}
                          {/* <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={handleRetry}
                              disabled={pageLoading || processingPayment}
                            >
                              Click to Retry
                            </button> */}
                          {/* </div> */}
                        </div>
                      )}

                      <div className="col-12 small text-muted mt-2">
                        <p className="mb-2">
                          NOTE: Only one card per person. VIP cards are the property of the Virginia
                          State Golf Association and the West Virginia Golf Association. Cards may be
                          recalled and privileges could be suspended if the terms on the card are
                          violated. Cards are non-transferable and non-refundable.
                        </p>
                        <p className="mb-2">
                          Please note that there will be a maximum 14-day exchange window, from date
                          of purchase, for golfers wishing to switch their VIP Card formats
                          (Paper to Digital OR Digital to Paper). If switching from Paper to Digital,
                          the paper booklet must be returned to the VSGA office, with no plays
                          redeemed, in order for the switch to be processed.
                        </p>
                        <p className="mb-0">
                          There will be a fee to replace all VIP Golf Card Booklets.
                        </p>
                      </div>

                      <div className="col-12 mt-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100 py-2"
                          disabled={loading || processingPayment || (finalTotal > 0 && pageLoading)}
                          onClick={finalTotal === 0 ? handleFreeOrder : handlePay}
                        >
                          {finalTotal === 0 ? "Complete Order" : "Submit Payment"}
                        </button>
                      </div>

                      <div className="col-12 mt-3">
                        {!formData.GiftPurchase ? (
                          <button
                            type="button"
                            className="btn btn-link text-decoration-none px-0"
                            onClick={() => navigate("/step5", { state: { golfer: location.state.golfer } })}
                          >
                            ← Back
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link text-decoration-none px-0"
                            onClick={() => { resetStep(1); navigate("/step1", { state: {} }); }}
                          >
                            ← Back
                          </button>
                        )}
                      </div>
                    </form>
                  </div>

                  {/* ── Right: order summary (shown above the form on phones/tablets) ── */}
                  <div className="col-lg-5 order-first order-lg-last">
                    <div className="bg-light border px-3 py-2 mb-3 text-uppercase fw-semibold d-lg-none" style={{ fontSize: "0.9rem" }}>
                      Order Summary
                    </div>
                    <aside className="order-summary-aside border rounded bg-white">
                      <button
                        type="button"
                        className={`order-summary-toggle d-flex d-lg-none w-100 justify-content-between align-items-center px-3 py-3 bg-transparent border-0 text-start ${summaryOpen ? "border-bottom" : ""}`}
                        onClick={() => setSummaryOpen((open) => !open)}
                        style={{ color: "#023822" }}
                        aria-expanded={summaryOpen}
                        aria-controls="order-summary-body"
                      >
                        <span className="fw-semibold" style={{ color: "#023822" }}>
                          <i className="bi bi-bag me-2" aria-hidden="true" />
                          {summaryOpen ? "Hide" : "Show"} details
                          <i className={`bi bi-chevron-${summaryOpen ? "up" : "down"} ms-2 small`} aria-hidden="true" />
                        </span>
                        <span className="fw-bold">${Number(formData.cart_total ?? cartTotal).toFixed(2)}</span>
                      </button>

                      <div id="order-summary-body" className={`p-3 p-md-4 ${summaryOpen ? "" : "d-none"} d-lg-block`}>
                      {products.map((product, index) => (
                        <div className="mb-3" key={index}>
                          <p className="mb-1 small text-uppercase fw-semibold">VIP Card Selection:</p>
                          <p className="mb-1">
                            {formData.GiftPurchase ? (
                              <><strong style={{ textTransform: "uppercase" }}>{formData.GiftVipPackage} Package</strong><br /></>
                            ) : (
                              <><strong style={{ textTransform: "uppercase" }}>{formData.vip_package || formData.GiftVipPackage} Package</strong><br /></>
                            )}
                            <span className="small text-uppercase text-muted">
                              {!formData.GiftPurchase && <>VIP Card Use: <strong>{formData.vip_card_type}</strong></>}
                            </span>
                          </p>

                          <div className="d-flex justify-content-between align-items-center small mb-2">
                            {golfer?.GiftPurchase ? (
                              <div className="d-flex align-items-center gap-2">
                                <span className="me-1">Quantity:</span>
                                <div className="input-group input-group-sm" style={{ width: "100px" }}>
                                  <button type="button" className="btn btn-outline-secondary"
                                    onClick={() => {
                                      const newQty = Math.max(1, (Number(formData.quantity) || 1) - 1);
                                      updateField("quantity", newQty as any);
                                      setGolfer((prev) => prev ? { ...prev, quantity: newQty as any } : prev);
                                      refreshCheckoutSession();
                                    }}>-</button>
                                  <input id="quantity" type="text" className="form-control text-center"
                                    value={formData.quantity ?? 1} disabled />
                                  <button type="button" className="btn btn-outline-secondary"
                                    onClick={() => {
                                      const newQty = (Number(formData.quantity) || 1) + 1;
                                      updateField("quantity", newQty as any);
                                      setGolfer((prev) => prev ? { ...prev, quantity: newQty as any } : prev);
                                      refreshCheckoutSession();
                                    }}>+</button>
                                </div>
                              </div>
                            ) : (
                              <span>Quantity: {formData.quantity ?? 1}</span>
                            )}
                            <span>${(Number(product.price || 0) * ((formData.quantity as any) ?? 1)).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}

                      <hr />

                      {formData.bagTag && (() => {
                        const TAX_RATE = 0.06;
                        const bagTagPreTax = Math.round((bagTagUnitPrice / (1 + TAX_RATE)) * 100) / 100;
                        const bagTagTax   = Math.round((bagTagUnitPrice - bagTagPreTax) * 100) / 100;
                        return (
                          <div className="mb-3 small">
                            <p className="mb-1 fw-semibold">Bag Tag</p>
                            <p className="mb-1 text-muted">Personalized Name: {formData.personalization}</p>
                            <div className="d-flex justify-content-between">
                              <span>Subtotal</span>
                              <span>${bagTagPreTax.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                              <span>Tax (6%)</span>
                              <span>${bagTagTax.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between fw-semibold mt-1">
                              <span>Bag Tag Total</span>
                              <span>${bagTagUnitPrice.toFixed(2)}</span>
                            </div>
                            <a href="#" className="d-inline-block mt-1" onClick={removeBagTag}>Remove</a>
                          </div>
                        );
                      })()}

                      {formData.donation && (
                        <>
                          <div className="mb-3 small">
                            <p className="mb-1 fw-semibold">Donation</p>
                            <p className="mb-1 text-muted">One-time donation</p>
                            <div className="d-flex justify-content-between">
                              <span>Quantity: 1</span>
                              <span>${Number(formData.donation_amount || 0).toFixed(2)}</span>
                            </div>
                            <a href="#" className="d-inline-block mt-1" onClick={removeDonation}>Remove</a>
                          </div>
                          <hr />
                        </>
                      )}

                      {!isGift && (
                        <>
                          <div className="mb-3">
                            <label className="form-label small" htmlFor="promoCode">Promo Code</label>
                            <div className="input-group">
                              <input type="text" id="promoCode" name="promoCode" className="form-control"
                                placeholder="Enter promo code" value={promoInput}
                                onChange={handlePromoInputChange} disabled={promoApplied || isApplyingPromo} />
                              <button type="button"
                                className="btn btn-primary d-flex align-items-center justify-content-center"
                                disabled={promoApplied || isApplyingPromo} onClick={applyPromoCode}>
                                {isApplyingPromo ? (
                                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Applying…</>
                                ) : "Apply"}
                              </button>
                            </div>
                          </div>

                          {promoError && <div className="text-danger small mt-1">{promoError}</div>}

                          {promoApplied && appliedPromoCode && (
                            <div className="alert alert-success py-2 mt-2 d-flex justify-content-between align-items-center">
                              <div className="small mb-0">Promo applied: <strong>{appliedPromoCode}</strong></div>
                              <button type="button" className="btn btn-link btn-sm text-decoration-none" onClick={removePromo}>Remove</button>
                            </div>
                          )}

                          {promoApplied && (
                            <div className="d-flex justify-content-between small">
                              <span>Discount</span>
                              <span>- ${Number(formData.promo_value || 0).toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="fw-semibold">Total</span>
                        <span className="fw-bold">${Number(formData.cart_total ?? cartTotal).toFixed(2)}</span>
                      </div>
                      </div>
                    </aside>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </section>
    </div>
  );
}